import React, { useState, useEffect, useMemo } from 'react';
import { getBlogPosts } from '../services/blogService';
import type { BlogPost, PageContent } from '../types';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { useToast } from '../hooks/useToast';
import { AdminControls } from '../components/AdminControls';
import DOMPurify from 'dompurify';
import { linkifyHtml } from '../utils/linkify';

export const BlogIndexPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const [pageContent, setPageContent] = useState<PageContent>(allContent.blogIndex);
  const { showToast } = useToast();

  useEffect(() => {
    setPageContent(allContent.blogIndex);
  }, [allContent.blogIndex]);

  useSeo(pageContent, 'blogIndex');

  const handleSave = async () => {
    await updatePageContent('blogIndex', pageContent);
    showToast('Blog Index page content saved!');
  };

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const fetchedPosts = await getBlogPosts(true);
      setPosts(fetchedPosts);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('app-rendered'));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, posts]);

  const categories = useMemo(() => {
    const catSet = new Set<string>(['All', 'Brand Name Ideas', 'Domain Name Ideas']);
    posts.forEach(p => p.categories.forEach(c => catSet.add(c)));
    return Array.from(catSet);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesCat =
        selectedCategory === 'All' || post.categories.includes(selectedCategory);
      const q = searchQuery.trim().toLowerCase();
      const matchesQuery =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.tags.some(t => t.toLowerCase().includes(q)) ||
        post.categories.some(c => c.toLowerCase().includes(q));
      return matchesCat && matchesQuery;
    });
  }, [posts, selectedCategory, searchQuery]);

  const sanitizedBody = pageContent?.body
    ? linkifyHtml(DOMPurify.sanitize(pageContent.body, { ADD_ATTR: ['class', 'target', 'rel'] }))
    : '';

  return (
    <>
      <div className="w-full max-w-6xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
        {isAdminMode && (
          <AdminControls
            pageId="blogIndex"
            pageContent={pageContent}
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}
        <div className="w-full max-w-3xl mx-auto mb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-6">
            {pageContent?.h1 || 'Brand Naming & Domain Strategy Blog'}
          </h1>
          {sanitizedBody && (
            <div
              className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-none mx-auto"
              dangerouslySetInnerHTML={{ __html: sanitizedBody }}
            />
          )}
        </div>

        {/* Search & Category Filter Controls */}
        <div className="mb-10 space-y-5">
          <div className="max-w-xl mx-auto">
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search 100+ brand name ideas & domain name guides (e.g., fashion, e-commerce, skincare)..."
              className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-gray-800"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(cat => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading posts...</div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No blog posts matched your filter. Try clearing your search query.
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between text-sm text-gray-500">
              <span>Showing <strong>{filteredPosts.length}</strong> expert naming &amp; domain guides</span>
              {selectedCategory !== 'All' && (
                <button
                  onClick={() => setSelectedCategory('All')}
                  className="text-indigo-600 hover:underline font-medium"
                >
                  Reset filter
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <div
                  key={post.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow duration-300"
                >
                  {post.featuredImage && (
                    <Link to={`/blog/${post.slug}/`}>
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-48 object-cover"
                      />
                    </Link>
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {post.categories.map(cat => (
                        <Link
                          key={cat}
                          to={`/blog/category/${encodeURIComponent(cat)}/`}
                          className="text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md transition-colors"
                        >
                          {cat}
                        </Link>
                      ))}
                    </div>
                    <Link to={`/blog/${post.slug}/`} className="block mb-2">
                      <h2 className="text-xl font-bold text-gray-900 hover:text-indigo-600 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <Link
                        to={`/blog/${post.slug}/`}
                        className="text-indigo-600 font-bold hover:underline"
                      >
                        Read Guide &amp; Check Domains &rarr;
                      </Link>
                      <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};
